import moment from 'moment';
import reactiveCashflow from '@riseupil/reactive-cashflow';
import { DateLocales } from '@riseupil/common-utils';
import { PerformanceMarks, performanceService } from '@/utils/PerformanceService';
import cashflowNormalizer from '../../utilities/cashflow-normalizer';
import CashflowUpdaterApi from '../../../api/CashflowUpdaterApi';
import cashflowViewConsts from '../../../constants/cashflow-view';

const actions = {
  async moveTransaction({ dispatch, commit, rootGetters }, { originalCategory, selectedCategory, transaction, categoryRuleType, valueForPredicate }) {
    performanceService.markStart(PerformanceMarks.MOVE_TRANSACTION);
    await dispatch('addEditedTransaction', transaction.transactionId);
    const moveToTracking = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING;
    const moveFromTracking = originalCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.TRACKING;
    const moveFromFixed = originalCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED;
    const moveFromExcludedExpense = originalCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.EXCLUDED_EXPENSE;
    const moveToVariable = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.VARIABLE;
    const moveToOneTimeSaving = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.ONE_TIME_SAVING;
    const moveToExcludedExpense = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.EXCLUDED_EXPENSE;
    const moveToExcludedIncome = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.EXCLUDED_INCOME;
    const moveToVariableIncome = selectedCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.VARIABLE_INCOME;
    if (moveFromFixed && moveToVariable) {
      await dispatch('moveFixedToVariable', {
        transactionId: transaction.transactionId,
      });
    } else if (moveToTracking || moveToOneTimeSaving) {
      await dispatch('changeTransactionCategory', {
        transaction,
        trackingCategoryId: selectedCategory.categoryId,
        matchBy: categoryRuleType,
        value: valueForPredicate,
        originalCategory,
      });
    } else if (moveFromTracking && moveToVariable) {
      await dispatch('moveFromTrackingToVariable', {
        transaction,
        expense: selectedCategory.expense,
        matchBy: categoryRuleType,
        value: valueForPredicate,
      });
    } else if (moveToExcludedExpense) {
      await dispatch('excludeExpense', {
        transactionId: transaction.transactionId,
      });
    } else if (moveToExcludedIncome) {
      await dispatch('excludeVariableIncome', {
        transactionId: transaction.transactionId,
      });
    } else if (moveToVariableIncome) {
      await dispatch('moveExcludedToVariableIncome', {
        transactionId: transaction.transactionId,
      });
    } else if (moveFromExcludedExpense) {
      await dispatch('moveExcludedToVariableExpense', {
        transactionId: transaction.transactionId,
      });
    } else {
      await dispatch('changeTransactionCategoryOld', {
        transaction,
        expense: selectedCategory.expense,
        applyTo: categoryRuleType,
        originalCategory,
      });
    }
    performanceService.markEndAndLog(PerformanceMarks.MOVE_TRANSACTION);
  },
  async changeEnvelopeExpenseCategory({ dispatch }, { envelopeId, sequenceId, expenseCategory, isIncome }) {
    await dispatch('applyAction', { action: 'changeEnvelopeExpenseCategory', params: { envelopeId, sequenceId, expenseCategory, isIncome } });
  },
  async changeTransactionCategoryOld({ dispatch }, { expense, transaction, applyTo, originalCategory }) {
    const params = {
      businessName: transaction.businessName,
      transactionId: transaction.transactionId,
      applyTo,
      expense,
      popEnvelope: originalCategory.cashflowCategory === cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED,
    };
    await dispatch('applyAction', { action: 'changeCategory', params });
  },
  async changeTransactionCategory({ dispatch }, { trackingCategoryId, matchBy, value, originalCategory }) {
    const popEnvelope = [
      cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED,
      cashflowViewConsts.CASHFLOW_CATEGORIES.FIXED_SAVING,
    ].includes(originalCategory.cashflowCategory);
    await dispatch('applyAction', {
      action: 'changeTransactionCategory',
      params: { trackingCategoryId, matchBy, value, popEnvelope },
    });
  },
  async moveFromTrackingToVariable({ dispatch }, { transaction, expense, matchBy, value }) {
    const moveFromTrackingParams = {
      matchBy,
      value,
    };
    await dispatch('applyAction', { action: 'moveFromTracking', params: moveFromTrackingParams });
  },
  async moveFixedToVariable({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'moveFixedToVariable', params: { transactionId } });
  },
  async moveExpenseToFixed({ commit, getters, dispatch }, { transactionId, monthsDiff = 1, expense }) {
    dispatch('applyAction', { action: 'moveExpenseToFixed', params: { transactionId, monthsDiff, expense } });
  },
  async moveIncomeToFixed({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'moveIncomeToFixed', params: { transactionId } });
  },
  async moveVariableIncomeToExcluded({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'excludeVariableIncome', params: { transactionId } });
  },
  async excludeExpense({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'excludeExpense', params: { transactionId } });
  },
  async moveVariableExpenseToExcluded({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'excludeVariableExpense', params: { transactionId } });
  },
  async moveExcludedToVariableIncome({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'moveExcludedToVariableIncome', params: { transactionId } });
  },
  async unexcludeTransaction({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'unexcludeTransaction', params: { transactionId } });
  },
  async excludeIncome({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'excludeIncome', params: { transactionId } });
  },
  async excludeVariableIncome({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'excludeVariableIncome', params: { transactionId } });
  },
  async moveExcludedToVariableExpense({ commit, getters, dispatch }, { transactionId }) {
    dispatch('applyAction', { action: 'moveExcludedToVariableExpense', params: { transactionId } });
  },
  async cancelEnvelope({ commit, dispatch }, { envelopeId, sequenceId, monthsAhead }) {
    dispatch('applyAction', { action: 'cancelEnvelope', params: { envelopeId, sequenceId, monthsAhead } });
  },
  async disableTrackingCategory({ commit, dispatch }, { envelopeId, trackingCategoryId }) {
    dispatch('applyAction', { action: 'disableTrackingCategory', params: { envelopeId, trackingCategoryId } });
  },
  async updateEnvelopeAmount({ commit, dispatch }, { envelopeId, amount, applyOnBudgetDate, sequenceId, isPermanent, monthsAhead }) {
    const params = { envelopeId, amount, applyOnBudgetDate, sequenceId, isPermanent, monthsAhead };
    dispatch('applyAction', { action: 'updateEnvelopeAmount', params });
  },
  async updateEnvelopeBalanceDate({ commit, dispatch }, { cashflowMonth, sequenceId, isPermanent }) {
    dispatch('applyAction', { action: 'updateEnvelopeBalanceDate', params: { cashflowMonth, sequenceId, isPermanent } });
  },
  async putTransactionToFixed({ commit, dispatch }, { envelopeId, sequenceId, transactionId, lastTransactions }) {
    dispatch('applyAction', { action: 'putTransactionToFixed', params: { envelopeId, sequenceId, transactionId, lastTransactions } });
  },
  async setComment({ commit, dispatch }, { transactionId, comment }) {
    dispatch('applyAction', { action: 'setTransactionComment', params: { transactionId, comment } });
  },
  async setSequenceComment({ commit, dispatch }, { sequenceId, comment }) {
    dispatch('applyAction', { action: 'setSequenceComment', params: { sequenceId, comment } });
  },
  async updateRiseupGoal({ commit, dispatch }, { budgetDate, amount }) {
    dispatch('applyAction', { action: 'updateRiseupGoal', params: { budgetDate, amount } });
  },
  async splitTransaction({ commit, dispatch }, { transactionId, slices, actionType, placementBeforeSplit }) {
    await dispatch('applyActionSync', { action: 'splitTransaction', params: { transactionId, slices, actionType, placementBeforeSplit } });
  },
  async indentTransaction({ dispatch, commit, rootState }, { transactionId, sourceBudgetDate, budgetDate }) {
    await dispatch('addEditedTransaction', transactionId);
    // Ensure the target month is loaded so optimus has somewhere to push. Without this, indents
    // to a future month (not preloaded by loadAllBudgets) leave the transaction stuck on the source
    // side until the backend round-trip completes and the customer navigates over.
    if (!rootState.cashflowView.allCashflows.some(c => c.budgetDate === budgetDate)) {
      await dispatch('cashflowView/loadBudgetDate', budgetDate, { root: true });
    }
    const { allCashflows, cashflowStartDay } = rootState.cashflowView;
    const updated = reactiveCashflow.optimus.indentTransaction(
      allCashflows,
      { transactionId, sourceBudgetDate, targetBudgetDate: budgetDate, cashflowStartDay },
      DateLocales.EN_IL,
    );
    const { presentedBudgetDate } = rootState.cashflowView;
    for (const cf of updated) {
      // setCashflowOptimistic also persists to localStorage (saveBudgetToStorage), so a refresh
      // before the backend version updates won't lose the optimistic change for the presented month.
      if (cf.budgetDate === presentedBudgetDate) {
        commit('cashflowView/setCashflowOptimistic', cf, { root: true });
      } else {
        commit('cashflowView/setCashflowForBudgetDate', { budgetDate: cf.budgetDate, cashflow: cf }, { root: true });
      }
    }
    await dispatch('cashflowView/resetBudgetTree', {}, { root: true });
    await CashflowUpdaterApi.indentTransaction({ transactionId, budgetDate });
  },
  async deactivateTrackingCategory({ commit, dispatch, rootGetters, rootState }, { envelopeId, trackingCategoryId }) {
    const { budgetDate, cashflowStartDay } = rootGetters['cashflowView/presentedBudget'];
    const created = moment.utc(budgetDate).date(cashflowStartDay).toISOString();
    await dispatch('applyAction', { action: 'deactivateTrackingCategory', params: { envelopeId, trackingCategoryId, created } });
    // Optimistically remove from future months too
    const futureCashflows = rootState.cashflowView.allCashflows
      .filter(cf => cf.budgetDate > budgetDate);
    for (const cf of futureCashflows) {
      const updated = reactiveCashflow.optimus.deactivateTrackingCategoryById(cf, { trackingCategoryId }, DateLocales.EN_IL);
      commit(
        'cashflowView/setCashflowForBudgetDate',
        { budgetDate: cf.budgetDate, cashflow: updated },
        { root: true },
      );
    }
  },
  async setIncomePredictionAmount({ commit, dispatch }, { budgetDate, amount }) {
    await dispatch('applyAction', { action: 'setIncomePredictionAmount', params: { budgetDate, amount } });
  },
  async applyAction({ commit, dispatch, rootGetters }, { action, params }) {
    if (params.transactionId) {
      await dispatch('addEditedTransaction', params.transactionId);
    }
    const currentCashflow = rootGetters['cashflowView/presentedBudget'];
    if (!_appliesToDifferentMonth(currentCashflow.budgetDate, action, params)) {
      const newCashflow = reactiveCashflow.optimus[action](currentCashflow, params, DateLocales.EN_IL);
      await commit('cashflowView/setCashflowOptimistic', newCashflow, { root: true });
      await dispatch('cashflowView/resetBudgetTree', {}, { root: true });
    }
    CashflowUpdaterApi[action](params);
  },
  async applyActionSync({ commit, dispatch, rootGetters }, { action, params }) {
    if (params.transactionId) {
      await dispatch('addEditedTransaction', params.transactionId);
    }
    const currentCashflow = rootGetters['cashflowView/presentedBudget'];
    if (!_appliesToDifferentMonth(currentCashflow.budgetDate, action, params)) {
      const newCashflow = reactiveCashflow.optimus[action](currentCashflow, params, DateLocales.EN_IL);
      await commit('cashflowView/setCashflowOptimistic', newCashflow, { root: true });
      await dispatch('cashflowView/resetBudgetTree', {}, { root: true });
    }
    await CashflowUpdaterApi[action](params);
  },
  addEditedTransaction({ commit }, transactionId) {
    cashflowNormalizer.addEditedTransaction(transactionId);
  },
};

function _appliesToDifferentMonth(budgetDate, action, params) {
  return (action === 'updateEnvelopeAmount' && !!params.applyOnBudgetDate && params.applyOnBudgetDate !== budgetDate);
}

export default {
  namespaced: true,
  actions,
};
